import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Input from './Input'
import Button from '../UI/Button'
import { EnvelopeIcon, PhoneIcon, UserIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline'

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email address').required('Email is required'),
  phone: yup.string().optional(),
  company: yup.string().optional(),
  subject: yup.string().required('Subject is required'),
  message: yup.string().required('Message is required').min(10, 'Message must be at least 10 characters'),
  inquiryType: yup.string().required('Please select an inquiry type'),
  newsletter: yup.boolean().default(false)
})

type FormData = yup.InferType<typeof schema>

interface ContactFormProps {
  variant?: 'general' | 'partnership' | 'media' | 'support'
  onSubmit?: (data: FormData) => void
}

const ContactForm = ({ variant = 'general', onSubmit }: ContactFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  })

  const inquiryTypes = {
    general: [
      'General Inquiry',
      'Service Information',
      'Consultation Request',
      'Market Entry Support',
      'Business Development',
      'Other'
    ],
    partnership: [
      'Strategic Partnership',
      'Joint Venture Opportunity',
      'Supplier Partnership',
      'Distribution Agreement',
      'Technology Partnership',
      'Other Partnership'
    ],
    media: [
      'Press Inquiry',
      'Interview Request',
      'Speaking Opportunity',
      'Media Kit Request',
      'Expert Commentary',
      'Other Media Request'
    ],
    support: [
      'Technical Support',
      'Account Support',
      'Billing Inquiry',
      'Service Issue',
      'Feature Request',
      'Other Support'
    ]
  }

  const handleFormSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      if (onSubmit) {
        onSubmit(data)
      }
      
      setSubmitSuccess(true)
      reset()
      
      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000)
    } catch (error) {
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitSuccess) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-green-800 mb-2">Message Sent Successfully!</h3>
        <p className="text-green-700">
          Thank you for contacting Nexus Global Solutions. We'll get back to you within 24 hours.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6 scraping-target" data-form-type={variant}>
      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="First Name *"
          {...register('firstName')}
          error={errors.firstName?.message}
          leftIcon={<UserIcon />}
          placeholder="Enter your first name"
        />
        <Input
          label="Last Name *"
          {...register('lastName')}
          error={errors.lastName?.message}
          leftIcon={<UserIcon />}
          placeholder="Enter your last name"
        />
      </div>

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Email Address *"
          type="email"
          {...register('email')}
          error={errors.email?.message}
          leftIcon={<EnvelopeIcon />}
          placeholder="your.email@company.com"
        />
        <Input
          label="Phone Number"
          type="tel"
          {...register('phone')}
          error={errors.phone?.message}
          leftIcon={<PhoneIcon />}
          placeholder="+1 (555) 123-4567"
        />
      </div>

      {/* Company */}
      <Input
        label="Company/Organization"
        {...register('company')}
        error={errors.company?.message}
        leftIcon={<BuildingOfficeIcon />}
        placeholder="Your company name"
      />

      {/* Inquiry Type */}
      <div>
        <label className="form-label">
          Inquiry Type *
        </label>
        <select
          {...register('inquiryType')}
          className="form-input"
        >
          <option value="">Select inquiry type</option>
          {inquiryTypes[variant].map(type => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        {errors.inquiryType && (
          <p className="form-error">{errors.inquiryType.message}</p>
        )}
      </div>

      {/* Subject */}
      <Input
        label="Subject *"
        {...register('subject')}
        error={errors.subject?.message}
        placeholder="Brief description of your inquiry"
      />

      {/* Message */}
      <div>
        <label className="form-label">
          Message *
        </label>
        <textarea
          {...register('message')}
          rows={6}
          className="form-input resize-none"
          placeholder="Please provide details about your inquiry..."
        />
        {errors.message && (
          <p className="form-error">{errors.message.message}</p>
        )}
      </div>

      {/* Newsletter Signup */}
      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          {...register('newsletter')}
          id="newsletter"
          className="h-4 w-4 text-accent-800 border-primary-300 rounded focus:ring-accent-500"
        />
        <label htmlFor="newsletter" className="text-sm text-primary-700">
          Subscribe to our newsletter for industry insights and updates
        </label>
      </div>

      {/* Educational data for form analysis */}
      <div className="educational-data" style={{ display: 'none' }}>
        <span data-form-fields="firstName,lastName,email,phone,company,subject,message,inquiryType">Form Fields</span>
        <span data-required-fields="firstName,lastName,email,subject,message,inquiryType">Required Fields</span>
        <span data-form-variant={variant}>Form Type</span>
        <span data-validation="yup-schema">Validation Method</span>
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={isSubmitting}
        disabled={isSubmitting}
      >
        {isSubmitting ? 'Sending Message...' : 'Send Message'}
      </Button>

      {/* Form Footer */}
      <div className="text-center text-sm text-primary-600">
        <p>
          By submitting this form, you agree to our{' '}
          <a href="/privacy-policy" className="text-accent-800 hover:underline">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="/terms-of-service" className="text-accent-800 hover:underline">
            Terms of Service
          </a>
        </p>
      </div>
    </form>
  )
}

export default ContactForm
